<script>
import AutoSuggest from "@/containers/auto-suggest/auto-suggest-panel";
import ButtonNavigation from "@/components/molecules/button-navigation";
import LazyImage from "@/components/atoms/lazy-image";
import ButtonIcon from "@/components/molecules/button-icon";
import SearchBar from "@/containers/auto-suggest/auto-suggest-search-bar";
import LinkTo from "@/components/atoms/link-to";
import {
  headerEmits,
  headerProps,
  useHeader
} from "@/components/organisms/header/mixin";
import Cart from "@/components/organisms/header/cart/default";
import Menu from "@/containers/menu";
import MonMagasin from "@/components/organisms/header/mon-magasin/default";
import AchatsExpress from "@/components/organisms/header/achats-express/default";
import MonCompte from "@/components/organisms/header/mon-compte";
import coraLogoCercle from "@/assets/images/cora-logo-cercle.svg";

export default {
  name: "HeaderLight",
  components: {
    LinkTo,
    SearchBar,
    ButtonIcon,
    LazyImage,
    Cart,
    Menu,
    AutoSuggest,
    ButtonNavigation,
    MonMagasin,
    AchatsExpress,
    MonCompte
  },
  props: {
    encartPanierVisible: {
      type: Boolean,
      required: true
    },
    showAutoSuggest: {
      type: Boolean,
      required: false,
      default: false
    },
    magasin: {
      type: Object,
      required: false,
      default: null
    },
    ...headerProps()
  },
  emits: [
    "askAuth",
    "autosuggestShown",
    "autosuggestClosed",
    "closePanel",
    "openPlanningPopin",
    "openOrcloseStoreLocator",
    "setEncartPanierVisible",
    ...headerEmits()
  ],
  methods: {
    hideMenu() {
      if (this.$refs.menu) {
        this.$refs.menu.hide();
      }
    },
    askAuth() {
      this.$emit("askAuth");
    },
    showAutosuggest() {
      this.$emit("autosuggestShown");
    },
    hideAutosuggest() {
      this.$emit("autosuggestClosed");
    }
  },
  computed: {
    magasinAriaLabel() {
      return this.magasin
        ? "Mon magasin " + this.magasin.attributes.magasin
        : "Mon magasin";
    }
  },
  setup(props, context) {
    const {
      logout,
      mounted,
      beforeUnmount,
      openMenu,
      menuIsOpened,
      miamPushZone
    } = useHeader(props, context);

    onBeforeUnmount(beforeUnmount);

    onMounted(mounted);

    return {
      logout,
      openMenu,
      coraLogoCercle,
      menuIsOpened,
      miamPushZone
    };
  }
};
</script>

<template>
  <transition name="header-sticky">
    <header
      role="banner"
      class="c-header c-header--light c-header--sticky"
      @click="hideAutosuggest()"
    >
      <div class="c-header__top">
        <div
          class="c-header__logo-wrapper"
          @click="$emit('closePanel')"
        >
          <LinkTo to="/">
            <LazyImage
              :src="coraLogoCercle"
              class="c-header__logo"
              alt="Logo Cora"
              width="51"
              height="42"
            />
          </LinkTo>
        </div>

        <SearchBar
          class="c-header__search-bar"
          :clear-on-init="false"
          @click.stop
          @focus="showAutosuggest()"
          @reset="hideAutosuggest()"
          @blur="showAutosuggest()"
        />

        <div
          v-if="showAutoSuggest"
          class="c-header__auto-suggest-wrapper"
        >
          <AutoSuggest
            class="c-header__auto-suggest"
            @closePanel="hideAutosuggest()"
          />
        </div>

        <div class="c-header__navigation">
          <ButtonNavigation
            ref="findShop"
            :notification-available="!isNational"
            icon-color="primary"
            icon-size="sm"
            icon-name="shop"
            open-on-hover
            :aria-label="magasinAriaLabel"
            :visible="showFindShop"
            class="c-header__button-navigation c-header__button-navigation--shop"
            @mouseover="$refreshShopData()"
          >
            <MonMagasin
              :is-national="isNational"
              :shop-name="shopName"
              :shop-phone="shopPhone"
              :shop-city="shopCity"
              :shop-zip-code="shopZipCode"
              :shop-address="shopAddress"
              :shopfirst-slot-available="shopfirstSlotAvailable"
              :shop-id="shopId"
              :web-alias="webAlias"
              :magasin="magasin"
              :is-pro="customer && customer.attributes.pro"
              @openPlanningPopin="$emit('openPlanningPopin')"
              @selectShopClicked="$emit('openOrcloseStoreLocator')"
            />
          </ButtonNavigation>
          <ButtonNavigation
            icon-name="prospectus"
            icon-color="primary"
            icon-size="sm"
            aria-label="Prospectus"
            :to="'/prospectus'"
            class="c-header__button-navigation"
          />
          <ButtonNavigation
            icon-name="mes-listes"
            icon-color="primary"
            icon-size="sm"
            open-on-hover
            aria-label="Mes listes de courses"
            class="c-header__button-navigation"
          >
            <AchatsExpress />
          </ButtonNavigation>
          <ButtonNavigation
            icon-name="cora-card"
            icon-color="primary"
            icon-size="md"
            aria-label="Ma fidélité"
            to="/mon-compte/ma-fidelite"
            class="c-header__button-navigation"
          />
          <ButtonNavigation
            :notification-available="customer!==null"
            icon-name="user-outline"
            aria-label="Mon Compte"
            class="c-header__button-navigation"
            icon-color="primary"
            icon-size="md"
            open-on-hover
            :aria-haspopup="customer===null"
            @click="askAuth()"
          >
            <MonCompte
              v-if="customer!==null"
              :first-name="customer.attributes.prenom"
              :is-pro="customer.attributes.pro"
              @logout="logout"
            />
          </ButtonNavigation>
        </div>
        <Cart
          class="u-ml-md"
          :total="montantTotalTTC"
          :is-loading="isPanierLoading"
          aria-label="Panier"
          @click="$emit('setEncartPanierVisible', true)"
        />

        <ButtonIcon
          :icon-name="menuIsOpened ? 'cross-thin' : 'hamburger'"
          icon-size="md"
          icon-color="primary"
          class="c-header__button-rayons"
          @click="openMenu"
        >
          <span class="u-color-primary"> Nos Rayons</span>
        </ButtonIcon>
      </div>

      <div
        v-if="showAutoSuggest"
        class="c-header__auto-suggest"
      />
      <div
        v-if="menuIsOpened"
        class="c-header__bottom"
      >
        <Menu
          ref="menu"
          :miam-push-zone="miamPushZone"
          class="c-header__menu"
        />
      </div>
    </header>
  </transition>
</template>
<style lang="scss" scoped>
@import "@/assets/styles/components/header.scss";
</style>
